import {
  Component,
  ElementRef,
  HostBinding,
  Input,
  OnDestroy,
  Renderer2,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
  forwardRef
} from "@angular/core";
import { NG_VALUE_ACCESSOR } from "@angular/forms";

import { DateService } from "@lantern/app/core/utils/date/date.service";
import {
  AnchorPositionStrategy,
  OverlayRef,
  OverlayService
} from "@lantern/app/core/utils/overlays";

import { AbstractDatepickerComponent } from "./abstract-datepicker.component";

/**
 * Usage
 * <input type="hidden" #startDate name="startInput" [(ngModel)]="entry.startDate">
 * <input type="hidden" #endDate name="endInput" [(ngModel)]="entry.endDate">
 * <ta-datepicker
 * 	[monthsAhead]="1"
 * 	[monthsBehind]="1"
 * 	[startInput]="startInput"
 * 	[endInput]="endInput">
 * </ta-datepicker>
 * @export
 * @class DatepickerComponent
 * @extends {AbstractDatepickerComponent}
 * @implements {OnDestroy}
 */

@Component({
  selector: "ta-datepicker",
  styles: [
    `
      .datepicker--disabled {
        opacity: 0.6;
        cursor: not-allowed;
      }
    `
  ],
  templateUrl: "./datepicker.template.html",
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DatepickerComponent),
      multi: true
    }
  ]
})
export class DatepickerComponent extends AbstractDatepickerComponent
  implements OnDestroy {
  @HostBinding("class.datepicker-wrapper") hostClass = true;

  @Input()
  calendarMonth: Date;

  @ViewChild("datepicker", { static: true })
  datepickerTemplateRef: TemplateRef<any>;

  @ViewChild("dateLabel", { static: true })
  dateLabel: ElementRef;

  @Input()
  get disabled(): boolean {
    return this._disabled;
  }
  set disabled(val: boolean) {
    if (val === this._disabled) {
      return;
    }
    this._disabled = val;
    if (this._disabled) {
      this.close();
    }
  }
  private _disabled: boolean = false;

  get opened(): boolean {
    return this.overlayRef != null;
  }

  private overlayRef?: OverlayRef;

  constructor(
    private overlaysService: OverlayService,
    private viewContainerRef: ViewContainerRef,
    renderer2: Renderer2,
    dateService: DateService
  ) {
    super(renderer2, dateService);
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this.close();
  }

  open(): void {
    if (this.opened || this.disabled) {
      return;
    }

    let positionStrategy: AnchorPositionStrategy = new AnchorPositionStrategy(
      this.dateLabel.nativeElement,
      ["bottom-left", "bottom-right", "top-left", "top-right"]
    );
    this.overlayRef = this.overlaysService.createTemplate(
      this.datepickerTemplateRef,
      this.viewContainerRef,
      positionStrategy
    );
  }

  close(): void {
    if (this.overlayRef == null) {
      return;
    }
    this.overlayRef.destroy();
    this.overlayRef = undefined;
  }
}
